import { useReporting } from '@/hooks/useReporting';
import useRouteInfo from '@/hooks/useRouteInfo';
import { ProviderType } from '@/reporting';
import logger from '@/utils/client-logger';
import { getCurrentScrollPercentage } from '@/utils/scroll';
import { useCallback, useEffect, useRef } from 'react';
import type { MktClickParams, MktExpParams } from '../types';

export const useDenimGlassesTrack = () => {
  const { trackEvent } = useReporting();
  const { getFromRouterInfo } = useRouteInfo();
  const enterTimer = useRef<number>(Date.now());
  // 记录最后的滚动状态，避免页面跳转时滚动位置被重置
  const lastScrollState = useRef({ viewAdvance: 0 });

  // 页面浏览埋点
  const pageView = useCallback(
    (_fromRouterInfo: any) => {
      trackEvent(
        {
          eventName: 'DenimGlassesPage_View',
          properties: {
            refer_page_name: _fromRouterInfo.refer_page_name,
            refer_url_path: _fromRouterInfo.refer_url_path,
          },
        },
        [ProviderType.SENSORS],
      );
    },
    [trackEvent],
  );

  // 页面离开埋点
  const pageLeave = useCallback(
    (_fromRouterInfo: any) => {
      const view_duration = Math.ceil((Date.now() - enterTimer.current) / 1000);
      // 使用保存的滚动状态，避免页面跳转时滚动位置被重置的问题
      const { viewAdvance } = lastScrollState.current;
      trackEvent(
        {
          eventName: 'DenimGlassesPage_Leave',
          properties: {
            refer_page_name: _fromRouterInfo.refer_page_name,
            refer_url_path: _fromRouterInfo.refer_url_path,
            view_advance: viewAdvance,
            view_duration,
          },
        },
        [ProviderType.SENSORS],
      );
    },
    [trackEvent],
  );

  // 营销位点击埋点
  const sdDenimGlassesPageMktClick = useCallback(
    (mktName: string, params: MktClickParams) => {
      try {
        trackEvent(
          {
            eventName: 'DenimGlassesPageMkt_Click',
            properties: {
              mkt_name: mktName,
              shop_sku: params.shop_sku || '',
              business_sku: params.business_sku || '',
              product_sku: params.product_sku || '',
              group_id: params.group_id || '',
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (e) {
        logger.error('DenimGlassesPageMkt_Click tracking error:', e);
      }
    },
    [trackEvent],
  );

  // 营销位曝光埋点
  const sdDenimGlassesPageMktExp = useCallback(
    (params: MktExpParams) => {
      try {
        trackEvent(
          {
            eventName: 'DenimGlassesPageMkt_Exp',
            properties: {
              mkt_name: params.mkt_name,
              shop_sku: params.shop_sku || '',
              business_sku: params.business_sku || '',
              product_sku: params.product_sku || '',
              group_id: params.group_id || '',
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (e) {
        logger.error('DenimGlassesPageMkt_Exp tracking error:', e);
      }
    },
    [trackEvent],
  );

  // 初始化埋点
  useEffect(() => {
    enterTimer.current = Date.now();
    const _fromRouterInfo = getFromRouterInfo();
    pageView(_fromRouterInfo);
    return () => {
      pageLeave(_fromRouterInfo);
    };
  }, []);
  // 定期更新滚动状态，避免页面跳转时丢失滚动信息
  useEffect(() => {
    const updateScrollState = () => {
      try {
        const viewAdvance = getCurrentScrollPercentage();
        lastScrollState.current = { viewAdvance };
      } catch (error) {
        // 忽略错误，使用默认值
        logger.error('Error updating scroll state:', error);
      }
    };

    // 立即更新一次
    updateScrollState();

    // 监听滚动事件，实时更新滚动状态
    const handleScroll = () => {
      updateScrollState();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return {
    pageView,
    pageLeave,
    sdDenimGlassesPageMktClick,
    sdDenimGlassesPageMktExp,
  };
};
